import type {
  GeoJSONMultiPolygon,
  PlotGeoBulkItem,
  PlotGeoBulkUpdateItem,
  PlotGeoLayoutUpdateItem,
  PlotGeoResponse,
  RowGeoBulkUpdateItem,
  RowGeoResponse,
  TrialGeoResponse,
} from "api/model";

import {
  doesBoundaryOverlapAny,
  doesRingExtendOutsideRing,
  fromBoundaryMultiPolygon,
  getBoundaryAreaHectares,
  hasBoundaryGeoJson,
  MIN_BOUNDARY_POINTS_TO_CLOSE,
  toBoundaryMultiPolygon,
  toLngLatRing,
} from "./boundaryUtils";
import {
  boundaryRing,
  findSnappedVertexId,
  NEAR_ZERO_LENGTH_EPSILON,
} from "./gridMeshGeometry";
import type {
  GridCell,
  GridCellSeed,
  GridMesh,
  GridVertex,
} from "./gridMeshTypes";
import {
  attachSavedRowBoundaries,
  meshToRowGeoBulkItems,
} from "./gridRowSplit";
import {
  DEFAULT_GRID_COLUMNS,
  DEFAULT_GRID_ROWS,
  getGridPointIndex,
  getPlotGridTrialLabelPoint,
  gridCellSeedKey,
  MIN_GRID_HALF_EXTENT,
  type GridTransform,
  type PlotGrid,
} from "./gridUtils";
import { distanceSq, type BoundaryPoint, type LngLatRing } from "./geo";

export const getTrialGeoLabel = (
  trial: TrialGeoResponse | null | undefined,
  trialId: number,
): string =>
  trial?.trial_name?.trim() || trial?.code?.trim() || `Trial ${trialId}`;

export const toPlotGridTrialLabelsGeoJson = (
  plots: PlotGeoResponse[],
  trialsById: Map<number, TrialGeoResponse>,
) => {
  const plotsByTrial = new Map<number, PlotGeoResponse[]>();

  plots.forEach((plot) => {
    if (plot.boundary == null) {
      return;
    }

    const trialPlots = plotsByTrial.get(plot.trial_id) ?? [];
    trialPlots.push(plot);
    plotsByTrial.set(plot.trial_id, trialPlots);
  });

  const features: Array<{
    type: "Feature";
    geometry: {
      type: "Point";
      coordinates: [number, number];
    };
    properties: {
      kind: "grid-trial-label";
      label: string;
      trialId: number;
    };
  }> = [];

  plotsByTrial.forEach((trialPlots, trialId) => {
    const grid = plotGeosToPlotGrid(trialPlots);
    if (grid == null) {
      return;
    }

    const coordinates = getPlotGridTrialLabelPoint(grid);
    if (coordinates == null) {
      return;
    }

    features.push({
      type: "Feature",
      geometry: {
        type: "Point",
        coordinates,
      },
      properties: {
        kind: "grid-trial-label",
        label: getTrialGeoLabel(trialsById.get(trialId), trialId),
        trialId,
      },
    });
  });

  return {
    type: "FeatureCollection" as const,
    features,
  };
};

/** True when at least one plot has a non-empty MultiPolygon boundary. */
export const hasPlotsGeo = (
  plots: PlotGeoResponse[] | null | undefined,
): boolean =>
  (plots ?? []).some((plot) => (plot.boundary?.coordinates.length ?? 0) > 0);

export interface PlotGridStats {
  areaHectares: number | null;
  plotCount: number;
}

/** Fallbacks for trials that do not specify a plot footprint. */
export const FALLBACK_TRIAL_PLOT_LENGTH_METERS = 4.5;
export const FALLBACK_TRIAL_ROW_SPACING_METERS = 0.9;

export interface InitialGridLayout {
  columns: number;
  plotLengthMeters: number;
  plotWidthMeters: number;
  rows: number;
}

const positiveOr = (value: number | null | undefined, fallback: number) =>
  value != null && Number.isFinite(value) && value > 0 ? value : fallback;

const formatTrialMeters = (value: number | null | undefined): string =>
  value != null && Number.isFinite(value) && value > 0
    ? `${value} m`
    : "Not set";

/** Trial footprint fields as stored on the trial record (no draw-time fallbacks). */
export const formatTrialPlotSizeInfo = (
  trial: TrialGeoResponse | null | undefined,
): string | null => {
  if (trial == null) {
    return null;
  }

  return `Trial settings: ${formatTrialMeters(trial.plot_length_m)} plot length, ${formatTrialMeters(trial.row_spacing_m)} row spacing × ${trial.rows_per_plot} rows/plot`;
};

/**
 * Map a plot's `range` and `min_row` to a lattice cell. Lattice row 0 is the
 * northern band and column 0 the western band, so (range 1, min_row 1) lands
 * in the lower-left cell at row `rowCount - 1`.
 */
export const getPlotLatticeIndices = (
  plot: Pick<PlotGeoResponse, "min_row" | "range">,
  rowCount: number,
  rowsPerPlot: number,
): { column: number; row: number } => ({
  column: Math.floor((plot.min_row - 1) / rowsPerPlot),
  row: rowCount - plot.range,
});

/**
 * Shape of the grid to draw for a trial: one row per plot `range`, one column
 * per `rows_per_plot` block of `max_row`. A column spans the crop rows in a
 * plot (`row_spacing_m` × `rows_per_plot`) and a row is one plot long.
 * Returns null when the trial has no plots — callers must require an upload
 * before drawing.
 */
export const getInitialGridLayout = (
  plots: PlotGeoResponse[],
  trial: TrialGeoResponse | null | undefined,
): InitialGridLayout | null => {
  if (plots.length === 0) {
    return null;
  }

  const rowsPerPlot = positiveOr(trial?.rows_per_plot, 1);

  let rows = 0;
  let columns = 0;
  plots.forEach((plot) => {
    rows = Math.max(rows, plot.range);
    columns = Math.max(columns, Math.ceil(plot.max_row / rowsPerPlot));
  });

  return {
    columns: columns > 0 ? columns : DEFAULT_GRID_COLUMNS,
    plotLengthMeters: positiveOr(
      trial?.plot_length_m,
      FALLBACK_TRIAL_PLOT_LENGTH_METERS,
    ),
    plotWidthMeters:
      positiveOr(trial?.row_spacing_m, FALLBACK_TRIAL_ROW_SPACING_METERS) *
      rowsPerPlot,
    rows: rows > 0 ? rows : DEFAULT_GRID_ROWS,
  };
};

/**
 * Map each plot onto its lattice cell using `range` (row) and the
 * `min_row` crop-row block (column). Seeds carry the plot's label and ids so
 * commit can write updates back to the same API records.
 */
export const getInitialCellSeeds = (
  plots: PlotGeoResponse[],
  trial: TrialGeoResponse | null | undefined,
): Record<string, GridCellSeed> => {
  const rowsPerPlot = positiveOr(trial?.rows_per_plot, 1);
  const rowCount = plots.reduce(
    (maxRange, plot) => Math.max(maxRange, plot.range),
    0,
  );
  const seeds: Record<string, GridCellSeed> = {};

  plots.forEach((plot) => {
    const { column, row } = getPlotLatticeIndices(plot, rowCount, rowsPerPlot);
    const key = gridCellSeedKey(row, column);
    seeds[key] = {
      externalPlotUuid: plot.external_plot_uuid,
      label: plot.plot_label?.trim() ?? "",
      maxRow: plot.max_row,
      minRow: plot.min_row,
      plotGeoId: plot.id,
      range: plot.range,
      varietyId: plot.variety_id,
    };
  });

  return seeds;
};

/**
 * Plot count and total spherical area (ha) for a lattice or committed mesh.
 * Area is the sum of per-cell polygon areas.
 */
export const getPlotGridStats = (grid: PlotGrid | null): PlotGridStats => {
  if (grid == null) {
    return { areaHectares: null, plotCount: 0 };
  }

  if (grid.isCommitted && grid.mesh != null) {
    let totalArea = 0;
    let areaCellCount = 0;

    grid.mesh.cells.forEach((cell) => {
      const area = getBoundaryAreaHectares(
        boundaryRing(cell.vertexIds, grid.mesh!.vertices),
      );
      if (area == null) {
        return;
      }
      totalArea += area;
      areaCellCount += 1;
    });

    return {
      areaHectares: areaCellCount > 0 ? totalArea : null,
      plotCount: grid.mesh.cells.length,
    };
  }

  const { columns, points, rows } = grid;
  if (rows == null || columns == null || rows < 1 || columns < 1) {
    return { areaHectares: null, plotCount: 0 };
  }

  let totalArea = 0;
  let areaCellCount = 0;

  for (let row = 0; row < rows; row += 1) {
    for (let column = 0; column < columns; column += 1) {
      const sw = points[getGridPointIndex(row, column, columns)];
      const se = points[getGridPointIndex(row, column + 1, columns)];
      const ne = points[getGridPointIndex(row + 1, column + 1, columns)];
      const nw = points[getGridPointIndex(row + 1, column, columns)];
      if (sw == null || se == null || ne == null || nw == null) {
        continue;
      }

      const area = getBoundaryAreaHectares([sw, se, ne, nw]);
      if (area == null) {
        continue;
      }
      totalArea += area;
      areaCellCount += 1;
    }
  }

  return {
    areaHectares: areaCellCount > 0 ? totalArea : null,
    plotCount: rows * columns,
  };
};

const signedRingArea = (points: BoundaryPoint[]): number => {
  let area = 0;
  const count = points.length;
  for (let index = 0; index < count; index += 1) {
    const current = points[index];
    const next = points[(index + 1) % count];
    if (current == null || next == null) {
      continue;
    }
    area +=
      current.longitude * next.latitude - next.longitude * current.latitude;
  }
  return area / 2;
};

/** Ensure vertex order is counter-clockwise (mesh convention). */
const ensureCounterClockwise = (points: BoundaryPoint[]): BoundaryPoint[] => {
  if (points.length < 3 || signedRingArea(points) >= 0) {
    return points;
  }

  return [...points].reverse();
};

const deriveTransformFromMesh = (mesh: GridMesh): GridTransform => {
  const vertices = Object.values(mesh.vertices);
  if (vertices.length === 0) {
    return {
      centerLatitude: 0,
      centerLongitude: 0,
      halfHeight: MIN_GRID_HALF_EXTENT,
      halfWidth: MIN_GRID_HALF_EXTENT,
      rotationDegrees: 0,
    };
  }

  let minLat = Number.POSITIVE_INFINITY;
  let maxLat = Number.NEGATIVE_INFINITY;
  let minLng = Number.POSITIVE_INFINITY;
  let maxLng = Number.NEGATIVE_INFINITY;

  vertices.forEach((vertex) => {
    minLat = Math.min(minLat, vertex.latitude);
    maxLat = Math.max(maxLat, vertex.latitude);
    minLng = Math.min(minLng, vertex.longitude);
    maxLng = Math.max(maxLng, vertex.longitude);
  });

  return {
    centerLatitude: (minLat + maxLat) / 2,
    centerLongitude: (minLng + maxLng) / 2,
    halfHeight: Math.max(MIN_GRID_HALF_EXTENT, (maxLat - minLat) / 2),
    halfWidth: Math.max(MIN_GRID_HALF_EXTENT, (maxLng - minLng) / 2),
    rotationDegrees: 0,
  };
};

/**
 * Rebuild a committed shared-vertex PlotGrid from independent plot polygons.
 * Corners that match within NEAR_ZERO_LENGTH_EPSILON share a vertex id.
 */
export const plotGeosToPlotGrid = (
  plots: PlotGeoResponse[],
  rows: readonly RowGeoResponse[] = [],
): PlotGrid | null => {
  const vertices: Record<string, GridVertex> = {};
  const cells: GridCell[] = [];
  let vertexCounter = 0;

  plots.forEach((plot) => {
    if (plot.boundary == null) {
      return;
    }

    const openRing = fromBoundaryMultiPolygon(plot.boundary);
    if (openRing == null) {
      return;
    }

    const orderedRing = ensureCounterClockwise(openRing);
    const vertexIds: string[] = [];

    orderedRing.forEach((point) => {
      const snappedId = findSnappedVertexId(vertices, point);
      if (snappedId != null) {
        vertexIds.push(snappedId);
        return;
      }

      vertexCounter += 1;
      const id = `gv-plot-${vertexCounter}`;
      vertices[id] = {
        id,
        latitude: point.latitude,
        longitude: point.longitude,
      };
      vertexIds.push(id);
    });

    if (vertexIds.length < 3) {
      return;
    }

    cells.push({
      id: `gc-plot-${plot.id}`,
      label: plot.plot_label?.trim() ?? "",
      externalPlotUuid: plot.external_plot_uuid,
      maxRow: plot.max_row,
      minRow: plot.min_row,
      plotGeoId: plot.id,
      range: plot.range,
      varietyId: plot.variety_id,
      vertexIds,
    });
  });

  if (cells.length === 0) {
    return null;
  }

  const mesh = attachSavedRowBoundaries({ cells, vertices }, rows);

  return {
    columns: null,
    invertColumnLabels: false,
    invertRowLabels: false,
    isCommitted: true,
    mesh,
    plotLengthMeters: null,
    plotWidthMeters: null,
    points: [],
    rows: null,
    transform: deriveTransformFromMesh(mesh),
  };
};

const cellToMultiPolygon = (
  cell: GridCell,
  vertices: GridMesh["vertices"],
): GeoJSONMultiPolygon | null =>
  toBoundaryMultiPolygon(boundaryRing(cell.vertexIds, vertices));

const plotLabelForSave = (cell: GridCell): string | null => {
  const trimmed = cell.label.trim();
  return trimmed === "" ? null : trimmed;
};

type PlotBulkDefaults = Pick<
  PlotGeoResponse,
  "max_row" | "min_row" | "variety_id"
>;

const toPlotGeoBulkItem = (
  boundary: GeoJSONMultiPolygon,
  cell: GridCell,
  defaults: PlotBulkDefaults,
): PlotGeoBulkItem => ({
  boundary,
  external_plot_uuid: crypto.randomUUID(),
  max_row: cell.maxRow ?? defaults.max_row,
  min_row: cell.minRow ?? defaults.min_row,
  plot_label: plotLabelForSave(cell),
  range: cell.range ?? 1,
  variety_id: cell.varietyId ?? defaults.variety_id,
});

/** Convert each mesh cell into a bulk-create plot item. */
export const meshToPlotGeoBulkItems = (
  mesh: GridMesh,
  defaults: PlotBulkDefaults,
): PlotGeoBulkItem[] => {
  const items: PlotGeoBulkItem[] = [];

  mesh.cells.forEach((cell) => {
    const boundary = cellToMultiPolygon(cell, mesh.vertices);
    if (boundary == null) {
      return;
    }

    items.push(toPlotGeoBulkItem(boundary, cell, defaults));
  });

  return items;
};

export interface PlotGridDiff {
  creates: PlotGeoBulkItem[];
  deletes: number[];
  updates: PlotGeoBulkUpdateItem[];
}

const openRingFromMultiPolygon = (
  boundary: GeoJSONMultiPolygon,
): BoundaryPoint[] | null => {
  const openRing = fromBoundaryMultiPolygon(boundary);
  if (openRing == null) {
    return null;
  }

  return ensureCounterClockwise(openRing);
};

/** True when rings match within snap epsilon, allowing a rotated start vertex. */
const ringsEqual = (a: BoundaryPoint[], b: BoundaryPoint[]): boolean => {
  if (a.length !== b.length) {
    return false;
  }

  for (let start = 0; start < b.length; start += 1) {
    let matches = true;
    for (let index = 0; index < a.length; index += 1) {
      const left = a[index];
      const right = b[(start + index) % b.length];
      if (
        left == null ||
        right == null ||
        distanceSq(left, right) > NEAR_ZERO_LENGTH_EPSILON
      ) {
        matches = false;
        break;
      }
    }
    if (matches) {
      return true;
    }
  }

  return false;
};

const boundariesEqual = (
  left: GeoJSONMultiPolygon | null | undefined,
  right: GeoJSONMultiPolygon | null | undefined,
): boolean => {
  if (left == null || right == null) {
    return false;
  }

  const leftRing = openRingFromMultiPolygon(left);
  const rightRing = openRingFromMultiPolygon(right);
  if (leftRing == null || rightRing == null) {
    return false;
  }

  return ringsEqual(leftRing, rightRing);
};

const labelsEqual = (
  left: string | null | undefined,
  right: string | null | undefined,
): boolean => (left?.trim() ?? "") === (right?.trim() ?? "");

const isPlotUnchanged = (
  cell: GridCell,
  boundary: GeoJSONMultiPolygon,
  serverPlot: PlotGeoResponse,
): boolean =>
  labelsEqual(cell.label, serverPlot.plot_label) &&
  boundariesEqual(boundary, serverPlot.boundary);

/**
 * Diff a working mesh against server plots for edit-save: create new cells,
 * update only cells whose geometry/label changed, delete orphans.
 */
export const diffPlotGrid = (
  mesh: GridMesh,
  serverPlots: PlotGeoResponse[],
): PlotGridDiff => {
  const creates: PlotGeoBulkItem[] = [];
  const updates: PlotGridDiff["updates"] = [];
  const retainedIds = new Set<number>();
  const serverById = new Map(
    serverPlots
      .filter((plot) => (plot.boundary?.coordinates.length ?? 0) > 0)
      .map((plot) => [plot.id, plot]),
  );

  const defaults: PlotBulkDefaults = {
    max_row: serverPlots[0]?.max_row ?? 1,
    min_row: serverPlots[0]?.min_row ?? 1,
    variety_id: serverPlots[0]?.variety_id ?? 1,
  };

  mesh.cells.forEach((cell) => {
    const boundary = cellToMultiPolygon(cell, mesh.vertices);
    if (boundary == null) {
      return;
    }

    if (cell.plotGeoId != null) {
      retainedIds.add(cell.plotGeoId);
      const serverPlot = serverById.get(cell.plotGeoId);
      if (serverPlot != null && isPlotUnchanged(cell, boundary, serverPlot)) {
        return;
      }

      updates.push({
        boundary,
        id: cell.plotGeoId,
        plot_label: plotLabelForSave(cell),
      });
      return;
    }

    creates.push(toPlotGeoBulkItem(boundary, cell, defaults));
  });

  const deletes = [...serverById.keys()].filter(
    (plotId) => !retainedIds.has(plotId),
  );

  return { creates, deletes, updates };
};

export interface PlotGridEditSavePlan {
  creates: PlotGeoBulkItem[];
  deletes: number[];
  layoutUpdates: PlotGeoLayoutUpdateItem[];
  plotUpdates: PlotGeoBulkUpdateItem[];
  rowUpdates: RowGeoBulkUpdateItem[];
}

const rowsForPlot = (
  mesh: GridMesh,
  plotGeoId: number,
  rowItemById: Map<number, RowGeoBulkUpdateItem>,
): RowGeoBulkUpdateItem[] =>
  (mesh.rows ?? []).flatMap((row) => {
    if (row.plotGeoId !== plotGeoId) {
      return [];
    }
    const item = rowItemById.get(row.rowGeoId);
    return item == null ? [] : [item];
  });

/**
 * Split an edit-save into plot, row, and layout requests.
 * A moved plot boundary is written with every row of that plot on
 * PATCH /plots-geo/layout/bulk so the server can check that the rows still
 * fill the new outline. Label-only plot edits and row edits whose plot
 * outline did not move stay on the separate bulk endpoints.
 */
export const planPlotGridEditSave = (
  mesh: GridMesh,
  serverPlots: PlotGeoResponse[],
  serverRows: readonly RowGeoResponse[],
): PlotGridEditSavePlan => {
  const diff = diffPlotGrid(mesh, serverPlots);
  const serverPlotById = new Map(serverPlots.map((plot) => [plot.id, plot]));
  const serverRowById = new Map(serverRows.map((row) => [row.id, row]));
  const rowItemById = new Map(
    meshToRowGeoBulkItems(mesh).map((item) => [item.id, item]),
  );
  const layoutUpdates: PlotGeoLayoutUpdateItem[] = [];
  const plotUpdates: PlotGeoBulkUpdateItem[] = [];
  const layoutPlotIds = new Set<number>();

  diff.updates.forEach((update) => {
    const serverPlot = serverPlotById.get(update.id);
    const boundaryChanged = !boundariesEqual(
      update.boundary,
      serverPlot?.boundary,
    );
    const labelChanged = !labelsEqual(
      update.plot_label,
      serverPlot?.plot_label,
    );
    const plotRows = rowsForPlot(mesh, update.id, rowItemById);

    if (boundaryChanged && plotRows.length > 0) {
      layoutPlotIds.add(update.id);
      layoutUpdates.push({
        boundary: update.boundary,
        id: update.id,
        rows: plotRows,
      });
    }

    if (!boundaryChanged || plotRows.length === 0 || labelChanged) {
      plotUpdates.push(update);
    }
  });

  const rowUpdates = (mesh.rows ?? []).flatMap((row) => {
    if (layoutPlotIds.has(row.plotGeoId)) {
      return [];
    }
    const item = rowItemById.get(row.rowGeoId);
    if (item == null) {
      return [];
    }
    const serverRow = serverRowById.get(row.rowGeoId);
    if (
      serverRow != null &&
      boundariesEqual(item.boundary, serverRow.boundary)
    ) {
      return [];
    }
    return [item];
  });

  return {
    creates: diff.creates,
    deletes: diff.deletes,
    layoutUpdates,
    plotUpdates,
    rowUpdates,
  };
};

const siteBoundaryToLngLatRing = (
  siteBoundary: GeoJSONMultiPolygon,
): LngLatRing | null => {
  const points = fromBoundaryMultiPolygon(siteBoundary);
  if (points == null) {
    return null;
  }

  return toLngLatRing(points);
};

const isPlotCellOutsideSiteBoundary = (
  cell: GridCell,
  vertices: GridMesh["vertices"],
  siteRing: LngLatRing,
): boolean => {
  const ring = toLngLatRing(boundaryRing(cell.vertexIds, vertices));
  if (ring.length < 3) {
    return false;
  }

  return doesRingExtendOutsideRing(ring, siteRing);
};

const hasUncommittedGridOutsideSiteBoundary = (
  grid: PlotGrid,
  siteRing: LngLatRing,
): boolean => {
  const { columns, points, rows } = grid;
  if (rows == null || columns == null || rows < 1 || columns < 1) {
    return false;
  }

  for (let row = 0; row < rows; row += 1) {
    for (let column = 0; column < columns; column += 1) {
      const sw = points[getGridPointIndex(row, column, columns)];
      const se = points[getGridPointIndex(row, column + 1, columns)];
      const ne = points[getGridPointIndex(row + 1, column + 1, columns)];
      const nw = points[getGridPointIndex(row + 1, column, columns)];
      if (sw == null || se == null || ne == null || nw == null) {
        continue;
      }

      if (doesRingExtendOutsideRing(toLngLatRing([sw, se, ne, nw]), siteRing)) {
        return true;
      }
    }
  }

  return false;
};

const hasCommittedMeshOutsideSiteBoundary = (
  mesh: GridMesh,
  siteRing: LngLatRing,
): boolean =>
  mesh.cells.some((cell) =>
    isPlotCellOutsideSiteBoundary(cell, mesh.vertices, siteRing),
  );

export const hasPlotsOutsideSiteBoundary = (
  mesh: GridMesh,
  siteBoundary: GeoJSONMultiPolygon | null | undefined,
): boolean => {
  if (siteBoundary == null || !hasBoundaryGeoJson(siteBoundary)) {
    return false;
  }

  const siteRing = siteBoundaryToLngLatRing(siteBoundary);
  if (siteRing == null) {
    return false;
  }

  return hasCommittedMeshOutsideSiteBoundary(mesh, siteRing);
};

export const hasPlotGridOutsideSiteBoundary = (
  grid: PlotGrid | null,
  siteBoundary: GeoJSONMultiPolygon | null | undefined,
): boolean => {
  if (grid == null) {
    return false;
  }

  if (siteBoundary == null || !hasBoundaryGeoJson(siteBoundary)) {
    return false;
  }

  const siteRing = siteBoundaryToLngLatRing(siteBoundary);
  if (siteRing == null) {
    return false;
  }

  if (grid.isCommitted && grid.mesh != null) {
    return hasCommittedMeshOutsideSiteBoundary(grid.mesh, siteRing);
  }

  return hasUncommittedGridOutsideSiteBoundary(grid, siteRing);
};

const OVERLAP_PAIR_SEPARATOR = "\0";

interface RingBounds {
  maxLatitude: number;
  maxLongitude: number;
  minLatitude: number;
  minLongitude: number;
}

interface RingPolygon {
  bounds: RingBounds;
  id: string;
  ring: BoundaryPoint[];
}

export interface MeshOverlapState {
  plotPairKeys: ReadonlySet<string>;
  rowPairKeys: ReadonlySet<string>;
}

const EMPTY_POLYGON_OVERLAP_PAIRS: ReadonlySet<string> = new Set();

export const EMPTY_MESH_OVERLAP: MeshOverlapState = {
  plotPairKeys: EMPTY_POLYGON_OVERLAP_PAIRS,
  rowPairKeys: EMPTY_POLYGON_OVERLAP_PAIRS,
};

const overlapPairKey = (id: string, otherId: string): string =>
  id < otherId
    ? `${id}${OVERLAP_PAIR_SEPARATOR}${otherId}`
    : `${otherId}${OVERLAP_PAIR_SEPARATOR}${id}`;

/** True when both rings list the same coordinates in the same order. */
const sameRing = (ring: BoundaryPoint[], other: BoundaryPoint[]): boolean => {
  if (ring.length !== other.length) {
    return false;
  }

  for (let index = 0; index < ring.length; index += 1) {
    const point = ring[index];
    const otherPoint = other[index];
    if (
      point == null ||
      otherPoint == null ||
      point.latitude !== otherPoint.latitude ||
      point.longitude !== otherPoint.longitude
    ) {
      return false;
    }
  }

  return true;
};

const ringBounds = (ring: BoundaryPoint[]): RingBounds | null => {
  const first = ring[0];
  if (first == null) {
    return null;
  }

  let maxLatitude = first.latitude;
  let maxLongitude = first.longitude;
  let minLatitude = first.latitude;
  let minLongitude = first.longitude;
  for (let index = 1; index < ring.length; index += 1) {
    const point = ring[index];
    if (point == null) {
      continue;
    }
    maxLatitude = Math.max(maxLatitude, point.latitude);
    maxLongitude = Math.max(maxLongitude, point.longitude);
    minLatitude = Math.min(minLatitude, point.latitude);
    minLongitude = Math.min(minLongitude, point.longitude);
  }

  return { maxLatitude, maxLongitude, minLatitude, minLongitude };
};

/**
 * True when the boxes have overlapping area. Plots that only share an edge
 * have touching boxes, and a shared edge is not an overlap, so those pairs
 * never reach the edge test.
 */
const boundsOverlap = (bounds: RingBounds, other: RingBounds): boolean =>
  bounds.minLongitude < other.maxLongitude &&
  other.minLongitude < bounds.maxLongitude &&
  bounds.minLatitude < other.maxLatitude &&
  other.minLatitude < bounds.maxLatitude;

const samePairSet = (
  pairs: ReadonlySet<string>,
  other: ReadonlySet<string>,
): boolean => {
  if (pairs.size !== other.size) {
    return false;
  }

  for (const pairKey of pairs) {
    if (!other.has(pairKey)) {
      return false;
    }
  }

  return true;
};

/** Closed rings plus a tight box. Open paths have no interior to overlap. */
const polygonsFrom = (
  items: ReadonlyArray<{ id: string; vertexIds: readonly string[] }>,
  vertices: GridMesh["vertices"],
): RingPolygon[] => {
  const polygons: RingPolygon[] = [];
  items.forEach((item) => {
    const ring = boundaryRing(item.vertexIds, vertices);
    // 1) Drop rings that cannot enclose an interior.
    if (ring.length < MIN_BOUNDARY_POINTS_TO_CLOSE) {
      return;
    }
    // 2) Store a tight box so a later test can reject distant polygons.
    const bounds = ringBounds(ring);
    if (bounds == null) {
      return;
    }
    polygons.push({ bounds, id: item.id, ring });
  });
  return polygons;
};

/**
 * True when a stored pair should not be kept as-is. Either polygon moved,
 * was deleted, or is no longer in the mesh.
 */
const shouldDropOverlapPair = (
  pairKey: string,
  changedIds: ReadonlySet<string>,
  removedIds: ReadonlySet<string>,
  afterById: ReadonlyMap<string, RingPolygon>,
): boolean => {
  const separatorIndex = pairKey.indexOf(OVERLAP_PAIR_SEPARATOR);
  if (separatorIndex === -1) {
    return true;
  }

  const leftId = pairKey.slice(0, separatorIndex);
  const rightId = pairKey.slice(separatorIndex + OVERLAP_PAIR_SEPARATOR.length);
  return (
    changedIds.has(leftId) ||
    changedIds.has(rightId) ||
    removedIds.has(leftId) ||
    removedIds.has(rightId) ||
    !afterById.has(leftId) ||
    !afterById.has(rightId)
  );
};

/**
 * Keep overlap pairs for polygons this change did not touch, and retest only
 * rings that were added or moved. A bounds test drops distant polygons before
 * the edge intersection test.
 */
const updateOverlapPairs = (
  previous: ReadonlySet<string>,
  before: RingPolygon[],
  after: RingPolygon[],
): ReadonlySet<string> => {
  // 1) Index rings by id so each ring can be compared with its previous coordinates.
  const beforeById = new Map(before.map((polygon) => [polygon.id, polygon]));
  const afterById = new Map(after.map((polygon) => [polygon.id, polygon]));

  // 2) New rings, and rings whose coordinates moved, are the only ones retested.
  const changedIds = new Set<string>();
  afterById.forEach((polygon, id) => {
    const prior = beforeById.get(id);
    if (prior == null || !sameRing(prior.ring, polygon.ring)) {
      changedIds.add(id);
    }
  });

  // 3) Deleted ids are not retested; their stored pairs are dropped below.
  const removedIds = new Set<string>();
  beforeById.forEach((_, id) => {
    if (!afterById.has(id)) {
      removedIds.add(id);
    }
  });

  // Nothing moved or disappeared, so the stored pairs are still valid.
  if (changedIds.size === 0 && removedIds.size === 0) {
    return previous;
  }

  // 4) Keep pairs whose polygons both kept the same ring.
  const next = new Set<string>();
  previous.forEach((pairKey) => {
    if (shouldDropOverlapPair(pairKey, changedIds, removedIds, afterById)) {
      return;
    }
    next.add(pairKey);
  });

  // 5) Retest each changed ring against every other ring.
  changedIds.forEach((id) => {
    const polygon = afterById.get(id);
    if (polygon == null) {
      return;
    }

    afterById.forEach((other) => {
      if (other.id === id) {
        return;
      }
      const pairKey = overlapPairKey(id, other.id);
      // Already recorded from the other polygon, or the boxes cannot intersect.
      if (next.has(pairKey) || !boundsOverlap(polygon.bounds, other.bounds)) {
        return;
      }
      // Crossings, containment, or an edge running through the other interior.
      if (doesBoundaryOverlapAny(polygon.ring, [other.ring])) {
        next.add(pairKey);
      }
    });
  });

  // 6) Reuse the previous set when membership did not change.
  if (previous.size === 0 && next.size === 0) {
    return previous;
  }
  if (samePairSet(previous, next)) {
    return previous;
  }
  return next;
};

/** Plot-cell rings compared between the mesh before and after a change. */
export const updatePlotOverlapPairs = (
  previous: ReadonlySet<string>,
  before: GridMesh,
  after: GridMesh,
): ReadonlySet<string> =>
  updateOverlapPairs(
    previous,
    polygonsFrom(before.cells, before.vertices),
    polygonsFrom(after.cells, after.vertices),
  );

/** Row rings. A mesh with no rows contributes nothing. */
export const updateRowOverlapPairs = (
  previous: ReadonlySet<string>,
  before: GridMesh,
  after: GridMesh,
): ReadonlySet<string> =>
  updateOverlapPairs(
    previous,
    polygonsFrom(before.rows ?? [], before.vertices),
    polygonsFrom(after.rows ?? [], after.vertices),
  );

/**
 * Refresh plot and row overlap after a committed geometry change.
 *
 * Only polygons whose rings differ between `before` and `after` are tested.
 * Pairs among polygons this change did not touch are kept, so a valid initial
 * mesh does not need a full scan.
 */
export const updateMeshOverlap = (
  previous: MeshOverlapState,
  before: GridMesh,
  after: GridMesh,
): MeshOverlapState => {
  // 1) Diff plots and rows separately. A plot edit does not rescan unchanged rows.
  const plotPairKeys = updatePlotOverlapPairs(
    previous.plotPairKeys,
    before,
    after,
  );
  const rowPairKeys = updateRowOverlapPairs(
    previous.rowPairKeys,
    before,
    after,
  );
  // 2) Same pair sets means the warning is unchanged.
  if (
    plotPairKeys === previous.plotPairKeys &&
    rowPairKeys === previous.rowPairKeys
  ) {
    return previous;
  }
  // 3) A mesh with no remaining overlaps shares one empty state object.
  if (plotPairKeys.size === 0 && rowPairKeys.size === 0) {
    return EMPTY_MESH_OVERLAP;
  }
  return { plotPairKeys, rowPairKeys };
};
